import { useCallback } from "react";
import * as storage from "@/lib/storage";
import { useAdditionalInfo } from "@/stores/domains/additionalInfo";
import { REDIRECT_PATH_KEY, REQUIRE_ADDITIONAL_INFO_KEY } from "../constants";
import {
  buildAdditionalInfoRedirectPath,
  canUseAsAuthReturnPath,
  clearAdditionalInfoRequirement,
  getCurrentReturnPath,
  getReturnPathname,
  isUnsafeRedirectPath,
  readAuthReturnPath,
  saveAuthReturnPath,
} from "../redirect";

interface UseRedirectPathReturn {
  saveRedirectPath: () => void;
  getRedirectPath: () => string;
  safeRedirect: (targetPath: string) => void;
}

export const useRedirectPath = (): UseRedirectPathReturn => {
  const saveRedirectPath = useCallback(() => {
    const currentPath = getCurrentReturnPath();
    const currentPathname = getReturnPathname(currentPath);
    const nonRedirectPaths = ["/login", "/", "/404", "/unauthorized", "/additional-info"];

    if (nonRedirectPaths.includes(currentPathname)) {
      return;
    }

    storage.safeSet(REDIRECT_PATH_KEY, currentPath);
  }, []);

  const getRedirectPath = useCallback(() => {
    const savedPath = storage.safeGet(REDIRECT_PATH_KEY);
    const currentPath = getCurrentReturnPath();
    const currentPathname = getReturnPathname(currentPath);
    const defaultPath = buildAdditionalInfoRedirectPath();
    const requiresAdditionalInfo = sessionStorage.getItem(REQUIRE_ADDITIONAL_INFO_KEY) === "true";

    // Check if user has additional info in localStorage
    // Note: user-additional-store is a Zustand persist store, which now automatically
    // has environment prefix added by createStore, so we need to use envStorage
    let hasAdditionalInfo = false;
    const currentEnv = import.meta.env.VITE_ENV || "development";
    const additionalInfoData = localStorage.getItem(`${currentEnv}-user-additional-store`);

    if (additionalInfoData) {
      try {
        const parsed = JSON.parse(additionalInfoData);
        hasAdditionalInfo = !!(
          parsed?.state?.organization &&
          parsed?.state?.location &&
          parsed?.state?.role
        );
      } catch (e) {
        console.error("Failed to parse additional info:", e);
      }
    }

    const nonRedirectPaths = ["/login", "/unauthorized"];

    if (requiresAdditionalInfo) {
      const returnPath =
        readAuthReturnPath() ?? [savedPath, currentPath].find(canUseAsAuthReturnPath);

      if (returnPath) {
        saveAuthReturnPath(returnPath);
      }

      clearAdditionalInfoRequirement();
      storage.safeRemove(REDIRECT_PATH_KEY);
      useAdditionalInfo.getState().reset();
      return buildAdditionalInfoRedirectPath(returnPath);
    }

    if (hasAdditionalInfo) {
      if (nonRedirectPaths.includes(currentPathname)) {
        const savedPathname = savedPath ? getReturnPathname(savedPath) : "";
        const targetPath = savedPath && !nonRedirectPaths.includes(savedPathname) ? savedPath : "/";
        storage.safeRemove(REDIRECT_PATH_KEY);
        return targetPath;
      }

      let redirectPath = savedPath || currentPath;

      if (nonRedirectPaths.includes(getReturnPathname(redirectPath))) {
        redirectPath = "/";
      }

      if (isUnsafeRedirectPath(redirectPath)) {
        storage.safeRemove(REDIRECT_PATH_KEY);
        return "/";
      }

      storage.safeRemove(REDIRECT_PATH_KEY);
      return redirectPath;
    }

    storage.safeRemove(REDIRECT_PATH_KEY);
    return defaultPath;
  }, []);

  const safeRedirect = useCallback((targetPath: string) => {
    const nonRedirectPaths = ["/login", "/unauthorized"];

    if (nonRedirectPaths.includes(getReturnPathname(targetPath))) {
      targetPath = "/additional-info";
    }

    if (getCurrentReturnPath() === targetPath) {
      return;
    }

    window.location.href = targetPath;
  }, []);

  return {
    saveRedirectPath,
    getRedirectPath,
    safeRedirect,
  };
};
